/**
 * Phạm vi chi nhánh của request hiện tại, đọc từ `localStorage` + `/v1/branches` (#19869).
 *
 * <p>Một nguồn duy nhất cho BA chỗ phải nói cùng một câu: bộ chọn ở thanh trên, nhãn "Đang xem" ở
 * các trang báo cáo, và widget doanh thu của Tổng quan. Hai nơi tự tính lấy là hai nơi sẽ lệch nhau.
 */
import { useQuery } from "@tanstack/react-query";

import { ACTIVE_BRANCH_KEY, branchesApi, branchesQueryKeys } from "../api/branches";
import {
  ALL_BRANCHES,
  decodeBranchClaim,
  resolveBranchScope,
  type BranchScope,
} from "./branchScope";

/** Cùng key với interceptor của `shared/api/client.ts`. */
const ACCESS_TOKEN_KEY = "addy.accessToken";

function readLocal(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Phạm vi đang áp dụng. `isLoading` để nơi gọi không in ra "tất cả chi nhánh" trong lúc danh sách
 * chi nhánh còn đang về — một nhãn đoán trước là đúng loại lỗi vé này đang sửa.
 */
export function useBranchScope(): BranchScope & { isLoading: boolean } {
  const q = useQuery({
    queryKey: branchesQueryKeys.all(),
    queryFn: () => branchesApi.list(),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const active = (q.data ?? [])
    .filter((b) => b.status === "ACTIVE")
    .map((b) => ({ id: b.id, name: b.name }));

  const scope = resolveBranchScope({
    stored: readLocal(ACTIVE_BRANCH_KEY),
    active,
    accessible: decodeBranchClaim(readLocal(ACCESS_TOKEN_KEY)),
  });

  return { ...scope, isLoading: q.isLoading };
}

/**
 * Ghi lựa chọn rồi tải lại trang.
 *
 * <p>Tải lại là chủ ý: header chi nhánh được gắn trong interceptor nên mọi dữ liệu đã nạp đều
 * thuộc phạm vi CŨ. Chọn "Tất cả chi nhánh" là XOÁ key — còn key là còn header, và còn header thì
 * server vẫn chỉ trả một chi nhánh.
 */
export function applyBranchSelection(value: string): void {
  try {
    if (value === ALL_BRANCHES) {
      localStorage.removeItem(ACTIVE_BRANCH_KEY);
    } else {
      localStorage.setItem(ACTIVE_BRANCH_KEY, value);
    }
  } catch {
    // Trình duyệt chặn storage: không ghi được thì cũng không có gì để tải lại cho đúng.
    return;
  }
  window.location.reload();
}
